import React, { useState, memo, useEffect, useCallback } from "react";
import {
  FieldGroup,
  FieldInput,
  LinkInput,
  LinkContainer,
  EmptyValue,
  FieldSelect,
  FieldLabel,
  CheckContainer,
  CheckInput,
  CheckSpan,
  CheckContainerInput,
  TextArea,
  ErrorMessage,
  RadioGroupOptions,
  RadioGroupContainer,
  Col,
  Break,
  FieldAutoCompleteGroup,
  AutoCompleteOptionContainer,
  AutoCompleteOption,
  InputContainer,
  HiddenSelect,
  MultiSelectPanel,
  MultiSelectToolbar,
  MultiSelectCounter,
  MultiSelectAction,
  MultiSelectGroup,
  MultiSelectGroupTitle,
  MultiSelectGroupCount,
  MultiSelectGrid,
  MultiSelectOption,
  MultiSelectBox,
  MultiSelectOptionLabel,
  MultiSelectEmpty,
} from "./styles";

import useOutsideClose from "hooks/useOutsideClose";
import { wordCounter, groupBy } from "helpers";
import Help from "components/Help";
export const InputField = memo(
  ({
    label,
    id,
    type = "text",
    register,
    required = false,
    error,
    value,
    onChange,
    placeholder,
    className,
    mode,
    readOnly,
    tabIndex,
    width,
    inputmode,
    cancelPaste,
    icon,
    at = false,
    prefix = "",
  }) => {
    /* `cancelPaste` es para los campos de confirmación: pegar los vacía de
       sentido, porque se copia el mismo error dos veces. Se bloquea también el
       drop, que es la misma copia hecha con otro gesto. */
    const onPaste = (e) => {
      if (cancelPaste) {
        e.preventDefault();
      }
    };
    return (
      <FieldGroup
        withError={error}
        className={`field-input-group ${className}`}
        width={width}
      >
        <FieldLabel
          withError={error}
          mode={mode}
          htmlFor={id}
          required={required}
        >
          {label}
        </FieldLabel>

        {readOnly && type === "link" ? (
          <LinkContainer>
            {value ? (
              <LinkInput
                href={`${value.startsWith(prefix) ? "" : prefix}${value}`}
                target="_blank"
                rel="noopener noreferrer"
                title={value}
              >
                {/* El texto se recorta con CSS: antes se cortaba a 42
                    caracteres y se le pegaba "..." siempre, incluso a un
                    usuario de Instagram de doce letras. */}
                {`${value.startsWith(prefix) ? "" : prefix}${value}`}
              </LinkInput>
            ) : (
              <EmptyValue>—</EmptyValue>
            )}
          </LinkContainer>
        ) : (
          // La caja la dibuja el contenedor, así el ícono queda adentro del
          // mismo recuadro que el texto en vez de colgar a la izquierda.
          <InputContainer mode={mode} withError={error}>
            {icon && icon}
            <FieldInput
              inputmode={inputmode}
              defaultValue={value}
              /* El valor de un campo de sólo lectura lo pone react-hook-form
                 sobre el elemento, así que desde acá no se sabe si vino vacío.
                 El placeholder resuelve el caso sin saberlo: el browser lo
                 muestra sólo cuando el campo está vacío, y con el gris de
                 placeholder, que es el mismo de los campos sin completar. */
              placeholder={readOnly ? "—" : placeholder}
              name={id}
              readOnly={readOnly}
              type={type}
              id={id}
              ref={register}
              mode={mode}
              tabIndex={tabIndex}
              autocomplete="off"
              onPaste={onPaste}
              onDrop={onPaste}
              /* Los filtros por texto de los toolbars cuelgan de acá: sin este
                 handler el campo se tipea pero nunca avisa a nadie. */
              onChange={(e) => onChange && onChange(id, e.target.value)}
            />
          </InputContainer>
        )}
        {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
      </FieldGroup>
    );
  }
);

export const TextAreaField = ({
  id,
  label = "",
  required = false,
  register,
  error,
  onCounter,
  value,
  readOnly,
  mode,
}) => {

  return (
    <FieldGroup withError={error}>
      {label !== "" && (
        <FieldLabel htmlFor={id} required={required} mode={mode}>
          {label}
        </FieldLabel>
      )}
      <TextArea
        readOnly={readOnly}
        placeholder={readOnly ? "—" : undefined}
        name={id}
        //id={id}
        ref={register}
        rows={5}
        defaultValue={value}
        //onChange={onChange}
        mode={mode}
        withError={error}
      />
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
    </FieldGroup>
  );
};

export const RadioGroupField = memo(
  ({ id, value, register, options, error, mode, readOnly, required }) => {
    return (
      <RadioGroupContainer>
        <RadioGroupOptions mode={mode}>
          {options.map((opt, index) => {
            return (
              <Col key={index}>
                <li>
                  <input
                    type="radio"
                    id={`${id}.value_${opt.value}`}
                    defaultChecked={opt.value === value}
                    ref={register}
                    value={opt.value}
                    disabled={readOnly}
                    name={`${id}.value`}
                  />

                  <label htmlFor={`${id}.value_${opt.value}`}>
                    <div className="check" />
                    <span>{opt.label}</span>
                    {opt.comment && <Help helpText={opt.comment} />}
                  </label>
                </li>
              </Col>
            );
          })}
        </RadioGroupOptions>
        {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
      </RadioGroupContainer>
    );
  }
);

export const CheckBoxesField = ({
  id,
  label,
  required,
  options,
  error,
  register,
  mode,
  twoLines,
  readOnly,
  onChange,
  withMetadata = true,
}) => {
  return (
    <FieldGroup className={`field-input-group`}>
      <CheckContainerInput mode={mode}>
        {label && (
          <FieldLabel htmlFor={id} required={required} mode={mode}>
            {label}
          </FieldLabel>
        )}

        {options.map((opt, index) => {
          return (
            <Col key={`${id}_${opt.value}`}>
              {/* El checkbox sólo aporta `checked`; sin estos hidden el submit
                  pierde el `value`/`label` de la opción y queda [{checked}].
                  El cuestionario los apaga porque repone la metadata en el
                  submit (mergeSectionsWithMetadata) y son muchas opciones. */}
              {withMetadata && opt.value !== undefined && (
                <input
                  type="hidden"
                  name={`${id}[${index}].value`}
                  ref={register}
                  value={opt.value}
                />
              )}
              {withMetadata && opt.label !== undefined && (
                <input
                  type="hidden"
                  name={`${id}[${index}].label`}
                  ref={register}
                  value={opt.label}
                />
              )}
              <CheckBoxField
                bold={false}
                register={register}
                value={opt.value}
                checked={opt.checked}
                label={opt.label}
                id={`${id}[${index}].checked`}
                twoLines={twoLines}
                readOnly={readOnly}
                mode={mode}
                onChange={(value) => {
                  onChange && onChange(opt.value, value);
                }}
              />
            </Col>
          );
        })}
        {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
      </CheckContainerInput>
    </FieldGroup>
  );
};

export const CheckBoxField = memo(
  ({
    id,
    label,
    value,
    secondary,
    link,
    error,
    onChange,
    register,
    bold = true,
    checked = false,
    showRow,
    twoLines,
    mode,
    readOnly,
    required,
    filter = false,
  }) => {
    const [internalChecked, setInternalChecked] = useState(checked);

    return (
      <CheckContainer
        checked={internalChecked}
        showRow={showRow}
        mode={mode}
        readOnly={readOnly}
        className={`checkbox-field`}
      >
        <CheckInput
          name={`${id}`}
          id={`${id}_${value}`}
          type="checkbox"
          onChange={(event) => {
            setInternalChecked(!internalChecked);
            onChange && onChange(event.target.checked);
          }}
          ref={register}
          defaultChecked={checked}
          mode={mode}
          readOnly={readOnly}
          required={required}
        />

        <CheckSpan readOnly={readOnly} showRow={showRow} mode={mode} />

        {twoLines && <Break show={twoLines} />}

        {label && (bold ? <b>{label}</b> : <label>{label}</label>)}
        <div>
          {secondary && <em>{secondary}</em>}
          {link && (
            <a href="/reglamento" target="_blank" rel="noopener noreferrer">
              {link}
            </a>
          )}
        </div>

        {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
      </CheckContainer>
    );
  }
);

export const ErrorMessageBar = memo((message) => <small>{message}</small>);

export function SelectField({
  id,
  label,
  placeholder = "Seleccionar un elemento",
  defaultOption,
  options,
  required,
  onChange,
  value,
  register,
  error,
  tabIndex,
  readOnly,
  mode,
  multiple,
}) {
  const [optionSelected, setOptionSelected] = useState();

  useEffect(() => {
    if (value) {
      setOptionSelected(value);
    }
  }, [value]);

  const handleOptionSelected = (target) => {
    if (multiple) {
      var selected = [];

      for (var i = 0, l = target.options.length; i < l; i++) {
        if (target.options[i].selected) {
          selected.push(options[i].value);
        }
      }

      setOptionSelected(selected);
    } else {
      setOptionSelected(target.value);
    }
  };

  return (
    <FieldGroup className={`field-input-group`} multiple={multiple}>
      <FieldLabel mode={mode} htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <FieldSelect
        multiple={multiple}
        value={optionSelected}
        tabIndex={tabIndex}
        placeholder={placeholder}
        witherror={error}
        name={id}
        id={id}
        ref={register}
        mode={mode}
        disabled={readOnly}
        onChange={(e) => {
          handleOptionSelected(e.target);

          onChange && onChange(id, e.target.value);
        }}
      >
        {defaultOption && (
          /* En sólo lectura la opción vacía no es una invitación a elegir:
             es "este campo quedó sin completar". */
          <option value="">{readOnly ? "—" : defaultOption}</option>
        )}
        {options.map((option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </FieldSelect>
      {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
    </FieldGroup>
  );
}

/**
 * Selector múltiple en columnas, agrupado por sección. Reemplaza al
 * `<select multiple>` nativo, que con muchas opciones obliga a scrollear a
 * ciegas y a usar ctrl+click para sumar (un click suelto borraba todo).
 *
 * El `<select multiple>` sigue existiendo (oculto) y es el que registra
 * react-hook-form, así el valor que llega al submit sigue siendo el mismo
 * array de `value`s de siempre.
 *
 * Cada opción puede traer:
 *   - `value`  (obligatorio) — lo que se guarda.
 *   - `label`  (obligatorio) — texto para el select nativo y fallback visual.
 *   - `title`  (opcional)    — texto de la fila, si difiere del label.
 *   - `group`  (opcional)    — encabezado bajo el que se agrupa la fila.
 */
export function MultiSelectField({
  id,
  label,
  options = [],
  value,
  register,
  error,
  required,
  mode,
  readOnly,
  onChange,
  columns = 3,
  emptyMessage = "No hay opciones disponibles",
}) {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    setSelected(Array.isArray(value) ? value : []);
  }, [value]);

  const updateSelected = (values) => {
    setSelected(values);

    onChange && onChange(id, values);
  };

  const toggleOption = (optionValue) => {
    if (readOnly) {
      return;
    }

    updateSelected(
      selected.includes(optionValue)
        ? selected.filter((item) => item !== optionValue)
        : selected.concat(optionValue)
    );
  };

  const allSelected =
    options.length > 0 && selected.length >= options.length;

  const toggleAll = () => {
    updateSelected(allSelected ? [] : options.map((option) => option.value));
  };

  const grouped = options.some((option) => option.group)
    ? groupBy(options, (option) => option.group || "Sin agrupar")
    : [{ name: null, value: options }];

  const optionRow = (option) => {
    const checked = selected.includes(option.value);

    return (
      <MultiSelectOption key={option.value} mode={mode} checked={checked}>
        <input
          type="checkbox"
          checked={checked}
          disabled={readOnly}
          onChange={() => toggleOption(option.value)}
        />
        <MultiSelectBox mode={mode} checked={checked} />
        <MultiSelectOptionLabel>
          {option.title || option.label}
        </MultiSelectOptionLabel>
      </MultiSelectOption>
    );
  };

  return (
    <FieldGroup className={`field-input-group`}>
      <FieldLabel mode={mode} required={required}>
        {label}
      </FieldLabel>

      {/* El que lee react-hook-form: mismo name, mismo array de siempre. */}
      <HiddenSelect
        multiple
        name={id}
        id={id}
        ref={register}
        value={selected}
        onChange={() => {}}
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </HiddenSelect>

      <MultiSelectPanel mode={mode} witherror={error}>
        <MultiSelectToolbar mode={mode}>
          <MultiSelectCounter>
            {selected.length} de {options.length} elegidos
          </MultiSelectCounter>

          {!readOnly && (
            <MultiSelectAction
              type="button"
              disabled={options.length === 0}
              onClick={toggleAll}
            >
              {allSelected ? "Quitar todos" : "Elegir todos"}
            </MultiSelectAction>
          )}
        </MultiSelectToolbar>

        {options.length === 0 ? (
          <MultiSelectEmpty>{emptyMessage}</MultiSelectEmpty>
        ) : (
          grouped.map((group) => (
            <MultiSelectGroup key={group.name || id} mode={mode}>
              {group.name && (
                <MultiSelectGroupTitle mode={mode}>
                  {group.name}
                  <MultiSelectGroupCount>
                    {
                      group.value.filter((option) =>
                        selected.includes(option.value)
                      ).length
                    }{" "}
                    de {group.value.length}
                  </MultiSelectGroupCount>
                </MultiSelectGroupTitle>
              )}
              <MultiSelectGrid columns={columns}>
                {group.value.map(optionRow)}
              </MultiSelectGrid>
            </MultiSelectGroup>
          ))
        )}
      </MultiSelectPanel>

      {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
    </FieldGroup>
  );
}

export function AutoCompleteField({
  label,
  id,
  register,
  required = false,
  error,
  value,
  setValue,
  mode,
  readOnly,
  tabIndex,
  parentId,
  onFetch,
}) {
  const [display, setDisplay] = useState(false);

  const [options, setOptions] = useState([value]);

  const [search, setSearch] = useState((value && value.label) || "");

  const { node } = useOutsideClose(() => setDisplay(false));

  useEffect(() => {
    register({ name: id });
    setValue(id, value || {});

    setSearch(value.label);
  }, [register, setValue, id, value]);

  const onChange = (label) => {
    setSearch(label);

    if (label.length >= 3) {
      onFetch(parentId, label).then(setOptions);

      setDisplay(true);
    } else {
      setDisplay(false);
      setOptions([]);
    }
  };

  const onSelected = (value) => {
    setSearch(value.label);
    setDisplay(false);

    setValue(id, value);
  };

  return (
    <FieldAutoCompleteGroup ref={node} withError={error}>
      <FieldLabel
        withError={error}
        mode={mode}
        htmlFor={id}
        required={required}
      >
        {label}
      </FieldLabel>
      <FieldInput
        value={search}
        placeholder="escriba por lo menos 3 letras"
        name={id}
        readOnly={readOnly}
        type="text"
        id={id}
        ref={register}
        mode={mode}
        autocomplete="off"
        tabIndex={tabIndex}
        onChange={(event) => onChange(event.target.value)}
      />

      {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}

      {display && (
        <AutoCompleteOptionContainer>
          {options
            .filter(
              ({ label }) =>
                label.toLowerCase().indexOf(search.toLowerCase()) > -1
            )
            .map((value, i) => {
              return (
                <AutoCompleteOption
                  onClick={() => onSelected(value)}
                  className="option"
                  key={i}
                  tabIndex={parseInt(tabIndex) + 1}
                >
                  {value.label}
                </AutoCompleteOption>
              );
            })}
        </AutoCompleteOptionContainer>
      )}
    </FieldAutoCompleteGroup>
  );
}

export function ColorField({ value }) {
  return (
    <div
      style={{
        padding: "2px",
        background: "#fff",
        borderRadius: "1px",
        boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
        display: "inline-block",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: "36px",
          height: "14px",
          borderRadius: "2px",
          backgroundColor: `${value}`,
        }}
      />
    </div>
  );
}
